import React, { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Row, Col, Badge, Button, Card } from "react-bootstrap";
import { FaYoutube } from "react-icons/fa";
import { MdLocationOn } from "react-icons/md";
import { Store } from "../context/store";
import apiRequest from "../utils/apiRequest";
import NavBar from "../components/Nav";
import BackTop from "../components/BackToTop";
import Call from "../components/Call";

// --- Sub-component for Dynamic Gallery ---
const PropertyGallery = ({ images = [] }) => {
  const count = images.length;

  if (count === 0)
    return (
      <div className="bg-secondary vh-50 d-flex align-items-center justify-content-center text-white">
        No Images Available
      </div>
    );

  if (count === 1) {
    return (
      <div className="container-fluid px-0">
        <img
          src={images[0]}
          className="w-100 object-fit-cover"
          style={{ height: "500px" }}
          alt="Property"
        />
      </div>
    );
  }

  if (count < 5) {
    return (
      <div className="container-fluid px-0">
        <div className="d-flex g-2" style={{ height: "500px" }}>
          {images.map((img, i) => (
            <div key={i} className="flex-fill border">
              <img
                src={img}
                className="w-100 h-100 object-fit-cover"
                alt={`view-${i}`}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Bento Grid for 5+ images
  return (
    <div className="container-fluid px-0">
      <Row className="g-2" style={{ height: "550px", margin: 0 }}>
        <Col md={6} className="h-100 p-0">
          <img
            src={images[0]}
            className="w-100 h-100 object-fit-cover"
            alt="Hero"
          />
        </Col>
        <Col md={6} className="h-100 p-0 d-none d-md-block">
          <Row className="g-2 h-100 m-0">
            {images.slice(1, 5).map((img, i) => (
              <Col key={i} md={6} className="h-50 p-1 position-relative">
                <img
                  src={img}
                  className="w-100 h-100 object-fit-cover rounded"
                  alt="Thumb"
                />
                {i === 3 && images.length > 5 && (
                  <div className="position-absolute inset-0 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center rounded m-1">
                    <span className="text-white fw-bold">
                      +{images.length - 5} More Photos
                    </span>
                  </div>
                )}
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </div>
  );
};

function Property() {
  const { id } = useParams();
  const store = useContext(Store);
  const [baseUrl] = store.url;
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCall, setShowCall] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const { data } = await apiRequest(`${baseUrl}/property/${id}`, "GET");
        setProperty(data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    loadData();
  }, [id, baseUrl]);

  if (loading)
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center">
        Loading...
      </div>
    );

  const displayValue = (value) =>
    value === null || value === undefined || value === "" ? "N/A" : value;

  // Logic to handle pricing from propertyUnits
  const allPrices =
    property?.propertyUnits
      ?.map((u) => u?.price)
      .filter((p) => typeof p === "number") || [];
  const minPrice = allPrices.length > 0 ? Math.min(...allPrices) : null;

  const address = property?.address || "";

  return (
    <div classNameName="container-fluid px-3" style={{ background: "white" }}>
      <title>{`RoyalMines Property - ${property?.title}`}</title>
      <meta
        name="description"
        content="Find the best real estate and properties in Abuja and Nigeria. Explore a wide range of residential and commercial properties, including houses, apartments, land, and more. Discover trusted real estate agents, listings, and resources to help you buy, sell, or rent properties in Abuja and across Nigeria."
      />
      <meta
        name="keywords"
        content="real estate, properties, Abuja, Nigeria, houses, apartments, land, mortgages, mortgage, real estate agents, listings, buy, sell, rent, residential, commercial, apartments, houses, land, real estate solutions, real estate company"
      />

      <NavBar properties={"active"} />
      <BackTop />

      <div className="container-xxl py-5">
        <div className="container">
          <div className="bg-light">
            <PropertyGallery images={property?.images} />

            <div className="container py-5">
              <Row className="g-4">
                {/* Main Content */}
                <Col lg={8}>
                  <div className="mb-4">
                    <Badge
                      bg="primary"
                      className="mb-2 px-3 py-2 text-uppercase"
                    >
                      {property?.status}
                    </Badge>
                    <h1 className="fw-bold display-6">{property?.title}</h1>
                    <p className="text-muted fs-5">
                      <MdLocationOn className="text-primary" />{" "}
                      {property?.address}
                    </p>
                  </div>

                  <h4 className="fw-bold mb-3">Description</h4>
                  <p className="text-secondary leading-relaxed mb-5">
                    {property?.description || "No description available."}
                  </p>

                  <h4 className="fw-bold mb-3">Features</h4>
                  {property?.features?.length ? (
                    <div className="d-flex flex-wrap gap-2 mb-5">
                      {property.features.map((feature, i) => (
                        <Badge
                          key={i}
                          bg="light"
                          className="text-dark border p-2 fw-normal px-3"
                        >
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted mb-5">N/A</div>
                  )}

                  <h4 className="fw-bold mb-3">Available Unit Types</h4>
                  <div className="space-y-3 mb-5">
                    {property?.propertyUnits?.length ? (
                      property.propertyUnits.map((unit, idx) => (
                        <Card
                          key={idx}
                          className="border-0 shadow-sm mb-3 rounded-4 overflow-hidden border-start border-primary border-5"
                        >
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <h6 className="fw-bold mb-1 text-primary">
                                  {displayValue(unit?.unit)}
                                </h6>
                                <small className="text-muted">
                                  {displayValue(unit?.bedrooms)} Bed |{" "}
                                  {displayValue(unit?.sittingRoom)} Living Room
                                  | {displayValue(unit?.quantity)} Units
                                  Available
                                </small>
                              </div>
                              <div className="text-end">
                                <span className="h5 fw-bold text-dark">
                                  {typeof unit?.price === "number"
                                    ? `₦${unit.price.toLocaleString()}`
                                    : "N/A"}
                                </span>
                              </div>
                            </div>

                            <div className="mt-3 d-flex flex-wrap gap-3 text-muted small">
                              <span>Toilet: {displayValue(unit?.toilet)}</span>
                              <span>
                                Bathroom: {displayValue(unit?.bathroom)}
                              </span>
                              <span>Dining: {displayValue(unit?.dining)}</span>
                              <span>
                                Size: {displayValue(unit?.propertySize)}
                              </span>
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    ) : (
                      <div className="text-muted">
                        No unit details available.
                      </div>
                    )}
                  </div>

                  <h4 className="fw-bold mb-3">Location</h4>
                  {address ? (
                    <div className="ratio ratio-16x9 rounded-4 overflow-hidden shadow-sm mb-4">
                      <iframe
                        title="Property location"
                        src={`https://www.google.com/maps?q=${encodeURIComponent(
                          address,
                        )}&output=embed`}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      ></iframe>
                    </div>
                  ) : (
                    <div className="text-muted mb-4">
                      Address not available.
                    </div>
                  )}

                  <h4 className="fw-bold mb-3">Nearby Landmarks</h4>
                  <div className="d-flex flex-wrap gap-2 mb-5">
                    {property?.landmarks?.length ? (
                      property.landmarks.map((mark, i) => (
                        <Badge
                          key={i}
                          bg="light"
                          className="text-dark border p-2 fw-normal px-3"
                        >
                          📍 {mark}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted">
                        No landmarks available.
                      </span>
                    )}
                  </div>
                </Col>

                {/* Sidebar */}
                <Col lg={4}>
                  <div className="sticky-top" style={{ top: "100px" }}>
                    <Card className="border-0 shadow-lg rounded-4 p-3">
                      <Card.Body>
                        <small className="text-muted">Starting from</small>
                        <h2 className="fw-bold text-primary mb-4">
                          {minPrice !== null
                            ? `₦${minPrice.toLocaleString()}`
                            : "Contact for Price"}
                        </h2>

                        <div className="d-grid gap-2">
                          <Button
                            variant="primary"
                            size="lg"
                            className="rounded-3 fw-bold py-3"
                            onClick={() => setShowCall(true)}
                          >
                            Book Inspection
                          </Button>
                          <Button
                            as={Link}
                            to="/contact"
                            variant="outline-dark"
                            size="lg"
                            className="rounded-3 fw-bold py-3"
                          >
                            Inquire
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>

                    {property?.videos?.length > 0 && (
                      <div className="mt-4">
                        <h6 className="fw-bold mb-3">
                          <FaYoutube className="text-danger" /> Virtual Tour
                          Available
                        </h6>
                        <div className="ratio ratio-16x9 rounded overflow-hidden shadow-sm">
                          <iframe
                            src={`https://www.youtube.com/embed/${property.videos[0].split("=")[1]}`}
                            title="YouTube video"
                            allowFullScreen
                          ></iframe>
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </div>
        <Call show={showCall} onShowChange={setShowCall} hideSection />
      </div>
    </div>
  );
}

export default Property;
